"use client";

import { useCallback, useEffect, useRef } from "react";
import { useSignalsStore } from "../store/signals";

/**
 * Hook for triggering and listening to signals across disconnected components.
 * Useful for communicating between components that are not directly connected in the React tree.
 *
 * @example
 * // Using a string key
 * const { trigger } = useSignal<{ userId: string }>("mention");
 *
 * // Using a Symbol for better encapsulation
 * const MENTION_SIGNAL = Symbol("mention");
 * const { trigger } = useSignal<{ userId: string }>(MENTION_SIGNAL);
 *
 * const handleClick = () => {
 *   trigger({ userId: "123" });
 * };
 *
 * // Component B - listen for the signal
 * const { listen } = useSignal<{ userId: string }>(MENTION_SIGNAL);
 *
 * useEffect(() => {
 *   listen((data) => {
 *     console.log("Received mention:", data.userId);
 *   });
 * }, [listen]);
 */
export function useSignal<T>(name: string | symbol) {
  const { emit, signals } = useSignalsStore();
  const lastSeenTimestamp = useRef<number>(0);
  const callbackRef = useRef<((data: T) => void) | null>(null);

  const trigger = useCallback(
    (data: T) => {
      emit(name, data);
    },
    [name, emit],
  );

  const listen = useCallback((callback: (data: T) => void) => {
    callbackRef.current = callback;
  }, []);

  const signal = name in signals ? signals[name as string | symbol] : undefined;

  useEffect(() => {
    if (
      signal &&
      signal.timestamp > lastSeenTimestamp.current &&
      callbackRef.current
    ) {
      lastSeenTimestamp.current = signal.timestamp;
      callbackRef.current(signal.data as T);
    }
  }, [signal?.timestamp, name]);

  return { trigger, listen };
}
